"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import {
  QrCode, Copy, Download, X, Check, Share2, Send,
  Search, Users, Smartphone, ExternalLink, ChevronRight,
  MessageSquare, Mail, Twitter, Facebook,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage, getInitials } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/auth-context";
import { db } from "@/lib/firebase";
import {
  collection, query, where, getDocs, orderBy,
  limit, startAt, endAt,
} from "firebase/firestore";
import {
  getOrCreateDMRoom,
  sendSharedContentMessage,
  getUserChatRooms,
  type ChatRoom,
  type SharedContent,
} from "@/services/chat";

// ─── Types ───────────────────────────────────────────────────────────────────

interface UserResult {
  uid: string;
  displayName: string;
  profileImageUrl: string;
  accountType?: string;
}

export interface SharePanelProps {
  /** The URL to share. Defaults to current page URL. */
  url?: string;
  /** Display title shown in share sheet / DM */
  title?: string;
  /** Subtitle shown under title in share card */
  subtitle?: string;
  /** Thumbnail image shown in QR card */
  imageUrl?: string;
  /** Trigger button variant */
  variant?: "outline" | "ghost" | "default" | "secondary";
  size?: "sm" | "icon" | "default" | "lg";
  className?: string;
  /** Icon only (no label) */
  iconOnly?: boolean;
  /** Override button label */
  label?: string;
  /** Content type for rich card sharing */
  contentType?: SharedContent["contentType"];
  /** Content ID for rich card sharing */
  contentId?: string;
  /** Called after a successful share action */
  onShare?: () => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const CANONICAL = process.env.NEXT_PUBLIC_APP_URL || "https://breakinbread.com";
const PINS_LS_KEY = "bb_share_pins";

function canonicalise(raw: string): string {
  try {
    // Resolve relative paths ("/music/work_123") against the canonical origin so
    // the shared/copied link is ALWAYS the complete absolute URL, never a bare
    // path. An already-absolute URL keeps its own path; we only force the
    // host/protocol to the canonical domain.
    const u = new URL(raw, CANONICAL);
    const c = new URL(CANONICAL);
    u.host = c.host;
    u.protocol = c.protocol;
    return u.toString();
  } catch {
    // Last-ditch: if `raw` is a bare path, prefix the canonical origin directly.
    if (raw.startsWith("/")) return CANONICAL.replace(/\/$/, "") + raw;
    return raw;
  }
}

function buildQrUrl(data: string, px = 400): string {
  return `https://api.qrserver.com/v1/create-qr-code/?size=${px}x${px}&data=${encodeURIComponent(data)}&bgcolor=ffffff&color=000000&qzone=2&format=png`;
}

function truncateUrl(url: string, max = 45): string {
  if (url.length <= max) return url;
  return url.slice(0, max - 1) + "…";
}

function getPinnedRooms(): string[] {
  if (typeof localStorage === "undefined") return [];
  try {
    return JSON.parse(localStorage.getItem(PINS_LS_KEY) || "[]");
  } catch { return []; }
}

function savePinnedRooms(ids: string[]) {
  localStorage.setItem(PINS_LS_KEY, JSON.stringify(ids));
}

// ─── WhatsApp / SMS / Email / Twitter / Facebook icons ───────────────────────

function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

// ─── External share targets ───────────────────────────────────────────────────

interface ShareTarget {
  id: string;
  label: string;
  icon: React.ReactNode;
  bgClass: string;
  textClass: string;
  action: (url: string, title: string, subtitle?: string) => void;
}

function buildTargets(
  handleCopy: () => void,
  handleNativeShare: () => void,
  canNativeShare: boolean,
): ShareTarget[] {
  const targets: ShareTarget[] = [
    {
      id: "copy",
      label: "Copy Link",
      icon: <Copy className="h-5 w-5" />,
      bgClass: "bg-muted",
      textClass: "text-foreground",
      action: handleCopy,
    },
  ];

  if (canNativeShare) {
    targets.push({
      id: "native",
      label: "Share via…",
      icon: <Smartphone className="h-5 w-5" />,
      bgClass: "bg-primary",
      textClass: "text-primary-foreground",
      action: handleNativeShare,
    });
  }

  targets.push(
    {
      id: "whatsapp",
      label: "WhatsApp",
      icon: <WhatsAppIcon className="h-5 w-5" />,
      bgClass: "bg-[#25D366]",
      textClass: "text-white",
      action: (url, title) =>
        window.open(`https://wa.me/?text=${encodeURIComponent(title + " " + url)}`, "_blank", "noopener"),
    },
    {
      id: "sms",
      label: "SMS",
      icon: <MessageSquare className="h-5 w-5" />,
      bgClass: "bg-green-600",
      textClass: "text-white",
      action: (url, title) =>
        window.open(`sms:?body=${encodeURIComponent(title + " " + url)}`),
    },
    {
      id: "email",
      label: "Email",
      icon: <Mail className="h-5 w-5" />,
      bgClass: "bg-blue-600",
      textClass: "text-white",
      action: (url, title) =>
        window.open(
          `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(url)}`,
        ),
    },
    {
      id: "twitter",
      label: "X (Twitter)",
      icon: <Twitter className="h-5 w-5" />,
      bgClass: "bg-black",
      textClass: "text-white",
      action: (url, title) =>
        window.open(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
          "_blank",
          "noopener,width=600,height=400",
        ),
    },
    {
      id: "facebook",
      label: "Facebook",
      icon: <Facebook className="h-5 w-5" />,
      bgClass: "bg-[#1877F2]",
      textClass: "text-white",
      action: (url) =>
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
          "_blank",
          "noopener,width=600,height=400",
        ),
    },
  );

  return targets;
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function SharePanel({
  url,
  title = "Share",
  subtitle,
  imageUrl,
  variant = "ghost",
  size = "icon",
  className,
  iconOnly = true,
  label,
  contentType,
  contentId,
  onShare,
}: SharePanelProps) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"external" | "inapp">("external");
  const [copied, setCopied] = useState(false);
  const [query_, setQuery_] = useState("");
  const [results, setResults] = useState<UserResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [sentTo, setSentTo] = useState<Set<string>>(new Set());
  const [sending, setSending] = useState<string | null>(null);
  const [myRooms, setMyRooms] = useState<ChatRoom[]>([]);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [pinnedIds, setPinnedIds] = useState<string[]>([]);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { toast } = useToast();
  const { user, userProfile } = useAuth();

  const resolvedUrl = canonicalise(
    url || (typeof window !== "undefined" ? window.location.href : CANONICAL)
  );
  const qrSrc = buildQrUrl(resolvedUrl);

  // ── Load pinned room IDs from localStorage ────────────────────────────────
  useEffect(() => {
    if (open) setPinnedIds(getPinnedRooms());
  }, [open]);

  // ── Load user's group/DM rooms for in-app share ───────────────────────────
  useEffect(() => {
    if (!open || !user || tab !== "inapp") return;
    setRoomsLoading(true);
    getUserChatRooms(user.uid)
      .then(rooms => setMyRooms(rooms))
      .catch(() => {/* silently ignore */})
      .finally(() => setRoomsLoading(false));
  }, [open, user, tab]);

  // ── Search users ──────────────────────────────────────────────────────────
  const searchUsers = useCallback(async (q: string) => {
    if (!q.trim()) { setResults([]); return; }
    setSearching(true);
    try {
      const term = q.trim();
      const termEnd = term.slice(0, -1) + String.fromCharCode(term.charCodeAt(term.length - 1) + 1);
      const snap = await getDocs(
        query(
          collection(db, "users"),
          orderBy("displayName"),
          startAt(term),
          endAt(termEnd),
          limit(10),
        )
      );
      const items: UserResult[] = [];
      snap.forEach(d => {
        if (d.id !== user?.uid) {
          const data = d.data();
          items.push({
            uid: d.id,
            displayName: data.displayName || data.name || d.id.slice(0, 8),
            profileImageUrl: data.profileImageUrl || data.photoURL || "",
            accountType: data.accountType,
          });
        }
      });

      if (items.length === 0) {
        const emailSnap = await getDocs(
          query(
            collection(db, "users"),
            orderBy("email"),
            startAt(term),
            endAt(termEnd),
            limit(5),
          )
        );
        emailSnap.forEach(d => {
          if (d.id !== user?.uid) {
            const data = d.data();
            items.push({
              uid: d.id,
              displayName: data.displayName || data.email?.split("@")[0] || d.id.slice(0, 8),
              profileImageUrl: data.profileImageUrl || data.photoURL || "",
              accountType: data.accountType,
            });
          }
        });
      }
      setResults(items);
    } catch (e) {
      console.error("User search error:", e);
    } finally {
      setSearching(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => searchUsers(query_), 300);
    return () => { if (searchTimeout.current) clearTimeout(searchTimeout.current); };
  }, [query_, searchUsers]);

  // ── Copy link ─────────────────────────────────────────────────────────────
  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(resolvedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({ title: "Link copied!" });
      onShare?.();
    } catch {
      toast({ title: "Copy failed", variant: "destructive" });
    }
  }, [resolvedUrl, toast, onShare]);

  // ── Native share ──────────────────────────────────────────────────────────
  const handleNativeShare = useCallback(async () => {
    if (typeof navigator === "undefined" || !navigator.share) {
      handleCopy();
      return;
    }
    try {
      await navigator.share({ title, text: subtitle || title, url: resolvedUrl });
      onShare?.();
    } catch (e: any) {
      if (e?.name !== "AbortError") toast({ title: "Share failed", variant: "destructive" });
    }
  }, [title, subtitle, resolvedUrl, handleCopy, toast, onShare]);

  // ── Download QR ───────────────────────────────────────────────────────────
  const handleDownload = () => {
    const a = document.createElement("a");
    a.href = buildQrUrl(resolvedUrl, 600);
    a.download = `qr-${title.replace(/\s+/g, "-").toLowerCase()}.png`;
    a.target = "_blank";
    a.rel = "noopener";
    a.click();
  };

  // ── Build shared content payload ──────────────────────────────────────────
  const buildSharedContent = useCallback((): SharedContent => ({
    contentType: contentType || "link",
    contentId,
    title,
    subtitle,
    imageUrl,
    link: resolvedUrl,
  }), [contentType, contentId, title, subtitle, imageUrl, resolvedUrl]);

  // ── Send to a chat room ───────────────────────────────────────────────────
  const sendToRoom = async (roomId: string, roomLabel: string) => {
    if (!user?.uid) { toast({ title: "Sign in to share", variant: "destructive" }); return; }
    setSending(roomId);
    try {
      const senderName = (userProfile as any)?.displayName || user.displayName || user.email || "Someone";
      const senderAvatar = (userProfile as any)?.profileImageUrl || user.photoURL || "";
      await sendSharedContentMessage(
        roomId,
        user.uid,
        senderName,
        buildSharedContent(),
        senderAvatar,
      );
      setSentTo(prev => new Set(prev).add(roomId));
      toast({ title: `Sent to ${roomLabel}!` });
      onShare?.();
    } catch (e) {
      console.error("Send to room error:", e);
      toast({ title: "Failed to send", variant: "destructive" });
    } finally {
      setSending(null);
    }
  };

  // ── Send DM to user (find/create DM room first) ───────────────────────────
  const sendToUser = async (target: UserResult) => {
    if (!user?.uid) { toast({ title: "Sign in to share", variant: "destructive" }); return; }
    setSending(target.uid);
    try {
      const senderName = (userProfile as any)?.displayName || user.displayName || user.email || "Someone";
      const senderAvatar = (userProfile as any)?.profileImageUrl || user.photoURL || "";
      const room = await getOrCreateDMRoom(
        user.uid, senderName, senderAvatar,
        target.uid, target.displayName, target.profileImageUrl,
      );
      await sendSharedContentMessage(
        room.id,
        user.uid,
        senderName,
        buildSharedContent(),
        senderAvatar,
      );
      setSentTo(prev => new Set(prev).add(target.uid));
      toast({ title: `Sent to ${target.displayName}!` });
      onShare?.();
    } catch (e) {
      console.error("Send DM error:", e);
      toast({ title: "Failed to send", variant: "destructive" });
    } finally {
      setSending(null);
    }
  };

  // ── Pin / unpin a room ────────────────────────────────────────────────────
  const togglePin = (roomId: string) => {
    setPinnedIds(prev => {
      const next = prev.includes(roomId)
        ? prev.filter(id => id !== roomId)
        : [roomId, ...prev];
      savePinnedRooms(next);
      return next;
    });
  };

  const canNativeShare = typeof navigator !== "undefined" && !!navigator.share;

  // Build share targets
  const targets = buildTargets(handleCopy, handleNativeShare, canNativeShare);

  // Rooms: pinned first, then rest sorted by last message
  const sortedRooms = [
    ...myRooms.filter(r => pinnedIds.includes(r.id)),
    ...myRooms.filter(r => !pinnedIds.includes(r.id)),
  ];

  // Separate group/artist rooms from DMs for display
  const groupRooms = sortedRooms.filter(r => r.type === "group" || r.type === "artist");
  const dmRooms = sortedRooms.filter(r => r.type === "direct");

  function roomLabel(room: ChatRoom): string {
    if (room.type === "group") return room.groupName || "Group";
    if (room.type === "artist") return room.artistName || "Artist Room";
    // DM: show the other participant's name
    const otherId = room.participants.find(id => id !== user?.uid);
    return otherId ? (room.participantNames?.[otherId] || "User") : "Chat";
  }

  function roomAvatar(room: ChatRoom): string {
    if (room.type === "group") return room.groupAvatar || "";
    if (room.type === "artist") return room.artistImageUrl || "";
    const otherId = room.participants.find(id => id !== user?.uid);
    return otherId ? (room.participantAvatars?.[otherId] || "") : "";
  }

  return (
    <>
      {/* Trigger */}
      <Button
        variant={variant}
        size={size}
        onClick={() => setOpen(true)}
        className={cn("shrink-0", className)}
        aria-label="Share"
        title="Share"
      >
        <Share2 className="h-4 w-4" />
        {!iconOnly && <span className="ml-1.5">{label || "Share"}</span>}
      </Button>

      {/* Full-screen panel */}
      {open && (
        <div
          className="fixed inset-0 flex flex-col bg-background"
          style={{ zIndex: 99999 }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
            <h2 className="text-base font-semibold">Share</h2>
            <button
              onClick={() => setOpen(false)}
              className="p-1.5 rounded-full border-2 border-foreground/40 text-foreground bg-background hover:bg-muted hover:border-foreground/70 transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content — clicking the empty area outside the content column closes */}
          <div
            className="flex-1 overflow-y-auto"
            onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
          >
            <div className="max-w-sm mx-auto px-4 py-5 space-y-5">

              {/* Entity preview card */}
              {(imageUrl || title) && (
                <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/50 border border-border/60">
                  {imageUrl && (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={imageUrl} alt={title} className="w-14 h-14 rounded-lg object-cover flex-shrink-0 shadow-sm" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-sm truncate">{title}</p>
                    {subtitle && <p className="text-xs text-muted-foreground truncate">{subtitle}</p>}
                    <p className="text-[10px] text-muted-foreground/60 truncate mt-0.5 font-mono">
                      {truncateUrl(resolvedUrl)}
                    </p>
                  </div>
                </div>
              )}

              {/* Tab pills */}
              <div className="flex gap-2">
                <button
                  onClick={() => setTab("external")}
                  className={cn(
                    "flex-1 py-2 rounded-lg text-sm font-medium transition-colors",
                    tab === "external"
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:bg-muted/80"
                  )}
                >
                  <ExternalLink className="h-3.5 w-3.5 inline mr-1.5" />
                  External
                </button>
                {user && (
                  <button
                    onClick={() => setTab("inapp")}
                    className={cn(
                      "flex-1 py-2 rounded-lg text-sm font-medium transition-colors",
                      tab === "inapp"
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground hover:bg-muted/80"
                    )}
                  >
                    <Users className="h-3.5 w-3.5 inline mr-1.5" />
                    In-App
                  </button>
                )}
              </div>

              {/* ── External Tab ── */}
              {tab === "external" && (
                <div className="space-y-3">
                  {/* Share target buttons in a grid */}
                  <div className="grid grid-cols-2 gap-2.5">
                    {targets.map(target => (
                      <button
                        key={target.id}
                        onClick={() => target.action(resolvedUrl, title, subtitle)}
                        className={cn(
                          "flex items-center gap-3 p-3.5 rounded-xl transition-opacity hover:opacity-90 active:opacity-75 text-left",
                          target.bgClass,
                          target.textClass,
                          target.id === "copy" && copied && "opacity-70",
                        )}
                      >
                        <div className={cn(
                          "h-9 w-9 rounded-full flex items-center justify-center flex-shrink-0",
                          target.id === "native" ? "bg-white/20" : "bg-black/10",
                        )}>
                          {target.id === "copy" && copied
                            ? <Check className="h-5 w-5" />
                            : target.icon}
                        </div>
                        <span className="font-semibold text-sm">
                          {target.id === "copy" && copied ? "Copied!" : target.label}
                        </span>
                      </button>
                    ))}
                  </div>

                  {/* QR code section */}
                  <div className="flex flex-col items-center rounded-2xl overflow-hidden shadow-xl border border-border bg-white mt-4">
                    {imageUrl && (
                      <div className="pt-5 pb-3 px-5 bg-white w-full flex justify-center">
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img src={imageUrl} alt={title} className="w-16 h-16 rounded-xl object-cover shadow-md" />
                      </div>
                    )}
                    <div className={cn("px-4", imageUrl ? "pb-4" : "py-4")}>
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        src={qrSrc}
                        alt="QR code"
                        width={220}
                        height={220}
                        className="block"
                        style={{ imageRendering: "pixelated" }}
                      />
                    </div>
                    <div className="w-full bg-black py-2 flex items-center justify-center gap-1.5">
                      <QrCode className="h-3 w-3 text-white/60" />
                      <span className="text-[10px] font-medium text-white/60 tracking-widest uppercase">
                        Scan to open
                      </span>
                    </div>
                  </div>

                  {/* Save QR */}
                  <Button variant="outline" className="w-full" onClick={handleDownload}>
                    <Download className="h-4 w-4 mr-2" />
                    Save QR Code
                  </Button>
                </div>
              )}

              {/* ── In-App Tab ── */}
              {tab === "inapp" && user && (
                <div className="space-y-4">

                  {/* Group chats first */}
                  {groupRooms.length > 0 && (
                    <div className="space-y-1.5">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        Groups
                      </p>
                      {groupRooms.slice(0, 6).map(room => {
                        const rLabel = roomLabel(room);
                        const rAvatar = roomAvatar(room);
                        const isSent = sentTo.has(room.id);
                        const isSending = sending === room.id;
                        const isPinned = pinnedIds.includes(room.id);
                        return (
                          <div
                            key={room.id}
                            className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-muted/50 transition-colors group"
                          >
                            <Avatar className="h-9 w-9 flex-shrink-0">
                              <AvatarImage src={rAvatar} alt={rLabel} />
                              <AvatarFallback className="text-xs" name={rLabel}>{getInitials(rLabel)}</AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{rLabel}</p>
                              <p className="text-xs text-muted-foreground capitalize">
                                {room.type === "artist" ? "Artist room" : "Group"}
                              </p>
                            </div>
                            <button
                              onClick={() => togglePin(room.id)}
                              className={cn(
                                "bb-hover-reveal transition-opacity text-xs px-1.5 py-0.5 rounded",
                                isPinned ? "text-primary" : "text-muted-foreground",
                              )}
                              title={isPinned ? "Unpin" : "Pin"}
                            >
                              {isPinned ? "★" : "☆"}
                            </button>
                            <Button
                              size="sm"
                              variant={isSent ? "outline" : "default"}
                              disabled={isSent || isSending}
                              onClick={() => sendToRoom(room.id, rLabel)}
                              className={cn("flex-shrink-0 text-xs h-7", isSent && "text-green-600 border-green-600")}
                            >
                              {isSent ? (
                                <><Check className="h-3.5 w-3.5 mr-1" />Sent</>
                              ) : isSending ? (
                                <><Send className="h-3.5 w-3.5 mr-1 animate-pulse" />Sending…</>
                              ) : (
                                <><Send className="h-3.5 w-3.5 mr-1" />Send</>
                              )}
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* DM chats */}
                  {dmRooms.length > 0 && (
                    <div className="space-y-1.5">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        Direct Messages
                      </p>
                      {dmRooms.slice(0, 6).map(room => {
                        const rLabel = roomLabel(room);
                        const rAvatar = roomAvatar(room);
                        const isSent = sentTo.has(room.id);
                        const isSending = sending === room.id;
                        const isPinned = pinnedIds.includes(room.id);
                        return (
                          <div
                            key={room.id}
                            className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-muted/50 transition-colors group"
                          >
                            <Avatar className="h-9 w-9 flex-shrink-0">
                              <AvatarImage src={rAvatar} alt={rLabel} />
                              <AvatarFallback className="text-xs" name={rLabel}>{getInitials(rLabel)}</AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{rLabel}</p>
                              <p className="text-xs text-muted-foreground">Direct</p>
                            </div>
                            <button
                              onClick={() => togglePin(room.id)}
                              className={cn(
                                "bb-hover-reveal transition-opacity text-xs px-1.5 py-0.5 rounded",
                                isPinned ? "text-primary" : "text-muted-foreground",
                              )}
                              title={isPinned ? "Unpin" : "Pin"}
                            >
                              {isPinned ? "★" : "☆"}
                            </button>
                            <Button
                              size="sm"
                              variant={isSent ? "outline" : "default"}
                              disabled={isSent || isSending}
                              onClick={() => sendToRoom(room.id, rLabel)}
                              className={cn("flex-shrink-0 text-xs h-7", isSent && "text-green-600 border-green-600")}
                            >
                              {isSent ? (
                                <><Check className="h-3.5 w-3.5 mr-1" />Sent</>
                              ) : isSending ? (
                                <><Send className="h-3.5 w-3.5 mr-1 animate-pulse" />Sending…</>
                              ) : (
                                <><Send className="h-3.5 w-3.5 mr-1" />Send</>
                              )}
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* User search */}
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Find a User
                    </p>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                      <Input type="search"
                        placeholder="Search by name…"
                        value={query_}
                        onChange={e => setQuery_(e.target.value)}
                        className="pl-9"
                        autoFocus={sortedRooms.length === 0}
                      />
                    </div>

                    <div className="space-y-1">
                      {searching && (
                        <p className="text-xs text-muted-foreground text-center py-3">Searching…</p>
                      )}
                      {!searching && query_.trim() && results.length === 0 && (
                        <p className="text-xs text-muted-foreground text-center py-3">No users found for "{query_}"</p>
                      )}
                      {!searching && !query_.trim() && sortedRooms.length === 0 && (
                        <p className="text-xs text-muted-foreground text-center py-3">
                          Type a name to find someone on Breakin' Bread
                        </p>
                      )}
                      {results.map(u => {
                        const isSent = sentTo.has(u.uid);
                        const isSending = sending === u.uid;
                        return (
                          <div
                            key={u.uid}
                            className="flex items-center gap-3 p-3 rounded-xl hover:bg-muted/50 transition-colors"
                          >
                            <Avatar className="h-10 w-10 flex-shrink-0">
                              <AvatarImage src={u.profileImageUrl} alt={u.displayName} />
                              <AvatarFallback name={u.displayName}>{getInitials(u.displayName)}</AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{u.displayName}</p>
                              {u.accountType && (
                                <p className="text-xs text-muted-foreground capitalize">{u.accountType}</p>
                              )}
                            </div>
                            <Button
                              size="sm"
                              variant={isSent ? "outline" : "default"}
                              disabled={isSent || isSending}
                              onClick={() => sendToUser(u)}
                              className={cn("flex-shrink-0", isSent && "text-green-600 border-green-600")}
                            >
                              {isSent ? (
                                <><Check className="h-3.5 w-3.5 mr-1" />Sent</>
                              ) : isSending ? (
                                <><Send className="h-3.5 w-3.5 mr-1 animate-pulse" />Sending…</>
                              ) : (
                                <><Send className="h-3.5 w-3.5 mr-1" />Send</>
                              )}
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      )}
    </>
  );
}
